import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Badge } from "@/components/ui/badge";
import { Menu, X, Lock } from "lucide-react";

const Navbar = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { label: "Urgence", to: "/panic", destructive: true },
    { label: "Ressources", to: "/resources" },
    { label: "Annuaire", to: "/directory" },
    { label: "Être accompagné", to: "/search", badge: "Bientôt" },
  ];

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-border bg-background/90 pt-[env(safe-area-inset-top)] backdrop-blur-lg">
      <div
          className="container mx-auto flex h-16 items-center"
        style={{
          paddingLeft: "max(1rem, env(safe-area-inset-left))",
          paddingRight: "max(1rem, env(safe-area-inset-right))",
        }}
      >
        <div className="cursor-pointer flex-shrink-0" onClick={() => navigate("/")}>
          <Logo />
        </div>

        <div className="flex-1" />

        <div className="flex items-center gap-2 md:gap-8 flex-shrink-0">
          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Button
                key={link.to}
                variant="ghost"
                onClick={() => navigate(link.to)}
                className={`min-h-11 px-2 text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  link.destructive
                    ? "text-destructive hover:text-destructive/80"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {link.label}
                {link.badge && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-accent/40 text-accent">
                    <Lock className="w-2.5 h-2.5 mr-0.5" />
                    {link.badge}
                  </Badge>
                )}
              </Button>
            ))}
          </div>

          <ThemeToggle />

          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <Button variant="outline" size="sm" onClick={() => signOut()}>
                Déconnexion
              </Button>
            ) : (
              <Button
                size="sm"
                className="bg-primary text-primary-foreground hover:bg-primary-600"
                onClick={() => navigate("/auth")}
              >
                Se connecter / S'inscrire
              </Button>
            )}
          </div>

          {/* Mobile burger */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-expanded={mobileOpen}
            aria-controls="mobile-navigation"
            aria-label={mobileOpen ? "Fermer le menu" : "Ouvrir le menu"}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div id="mobile-navigation" className="max-h-[calc(100dvh-4rem-env(safe-area-inset-top))] overflow-y-auto overscroll-contain border-t bg-background/95 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4 backdrop-blur-lg md:hidden fade-in">
          {navLinks.map((link) => (
            <Button
              key={link.to}
              variant="ghost"
              onClick={() => {
                navigate(link.to);
                setMobileOpen(false);
              }}
              className={`flex min-h-11 w-full justify-start px-2 text-left text-base font-medium ${
                link.destructive ? "text-destructive" : "text-foreground"
              }`}
            >
              {link.label}
              {link.badge && (
                <Badge variant="outline" className="ml-2 text-[10px] px-1.5 py-0 h-4 border-accent/40 text-accent">
                  <Lock className="w-2.5 h-2.5 mr-0.5" />
                  {link.badge}
                </Badge>
              )}
            </Button>
          ))}
          <div className="pt-2 border-t space-y-2">
            {user ? (
              <Button variant="outline" className="w-full" onClick={() => { signOut(); setMobileOpen(false); }}>
                Déconnexion
              </Button>
            ) : (
              <Button className="w-full" onClick={() => { navigate("/auth"); setMobileOpen(false); }}>
                Se connecter / S'inscrire
              </Button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
